import { Box, Grid } from "@mui/material";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAssets } from "../../assets/contexts/AssetsContext";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import Pagination from "../../common/components/Pagination";
import { useSources, useUserProjectRole } from "../api/sources";
import { AddSourceModal } from "../components/AddSourceModal";
import { MissingSourcesPlaceholder } from "../components/MissingSourcesPlaceholder";
import { SourceItem } from "../components/SourceItem";

export const Sources = () => {
  const { t } = useTranslation("sources");
  const { sources, createSource, loading } = useSources();
  const { canAddSources } = useUserProjectRole();
  const { refetch: refetchAssets } = useAssets();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(12);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleCreateSource = async (source) => {
    await createSource(source);
    await refetchAssets();

    handleCloseModal();
  };

  // Calculate pagination
  const totalItems = sources?.length || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedSources = sources?.slice(startIndex, endIndex) || [];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box>
      <PageHeader
        title={t("title")}
        description={t("description")}
        appendActions={
          canAddSources ? (
            <Box sx={{ display: "flex", gap: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/sources/manual-import")}
                iconLeft={<Plus size={20} />}
              >
                {t("manuallyImport")}
              </Button>
              <Button
                variant="contained"
                onClick={handleOpenModal}
                iconLeft={<Plus size={20} />}
              >
                {t("addSource")}
              </Button>
            </Box>
          ) : null
        }
      />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <Spinner animation="border" />
        </Box>
      ) : sources?.length === 0 ? (
        <Box>
          <MissingSourcesPlaceholder
            handleOpenModal={handleOpenModal}
            canAddSources={canAddSources}
          />
        </Box>
      ) : (
        <>
          <Grid container spacing={4}>
            {paginatedSources.map((source) => (
              <SourceItem key={source.id} source={source} />
            ))}
          </Grid>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setCurrentPage(1);
              }}
            />
          )}
        </>
      )}

      <AddSourceModal
        open={modalOpen}
        onClose={handleCloseModal}
        createSource={handleCreateSource}
      />
    </Box>
  );
};
